import { useEffect, useState } from "react";
import { TextField } from "@mui/material";
import {
    LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
    PieChart, Pie, Cell, Legend, ResponsiveContainer,
    BarChart, Bar
} from "recharts";
import { FaRupeeSign, FaFileInvoice } from "react-icons/fa";
import API from "../../services/api";
function VoucherDashboard() {

    const [data, setData] = useState({
        totalVouchers: 0,
        totalAmount: 0,
        vouchersPerDay: [],
        vouchersByParty: [],
        categorySplit: []
    });
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [paidTo, setPaidTo] = useState("");
    const [hoveredCard, setHoveredCard] = useState(null);
    const loadData = async (f = fromDate, t = toDate, p = paidTo) => {
        try {
            let query = [];

            if (f) query.push(`fromDate=${f}`);
            if (t) query.push(`toDate=${t}`);
            if (p) query.push(`paidTo=${p}`);

            // 🔹 CURRENT DATA
            const res = await API.get(`/dashboard/voucher?${query.join("&")}`);

            setData(res.data);

            // 🔥 CALCULATE PREVIOUS RANGE
            if (f && t) {
                const from = new Date(f);
                const to = new Date(t);

                const diffDays = Math.ceil((to - from) / (1000 * 60 * 60 * 24));

                const prevFrom = new Date(from);
                prevFrom.setDate(prevFrom.getDate() - diffDays);

                const prevTo = new Date(to);
                prevTo.setDate(prevTo.getDate() - diffDays);

                const format = d => d.toISOString().split("T")[0];

                const prevRes = await API.get(
                    `/dashboard/voucher?fromDate=${format(prevFrom)}&toDate=${format(prevTo)}`
                );

                // 🔥 TREND CALCULATION
                const calcTrend = (current, prev) => {
                    if (!prev || prev === 0) return { value: 100, isUp: true };

                    const change = ((current - prev) / prev) * 100;

                    return {
                        value: Math.abs(change),
                        isUp: change >= 0
                    };
                };

                setTrend({
                    totalVouchers: calcTrend(
                        res.data.totalVouchers,
                        prevRes.data.totalVouchers
                    ),
                    totalAmount: calcTrend(
                        res.data.totalAmount,
                        prevRes.data.totalAmount
                    )
                });
            }

        } catch (err) {
            console.error("Dashboard error:", err);
        }
    };
    
    useEffect(() => {
        const today = new Date();
        const last7 = new Date();
        last7.setDate(today.getDate() - 7);

        const format = d => d.toISOString().split("T")[0];

        const from = format(last7);
        const to = format(today);

        setFromDate(from);
        setToDate(to);
        loadData(from, to);
        setTrend({
            totalVouchers: { value: 12.5, isUp: true },
            totalAmount: { value: -8.3, isUp: false }
        });
        
       
    }, []);

    const [trend, setTrend] = useState({});
    /*if (!data) return <div>Loading...</div>;*/

    const avgAmount = data.totalVouchers
        ? Math.round(data.totalAmount / data.totalVouchers)
        : 0;
    const burnRate = data.vouchersPerDay && data.vouchersPerDay.length > 0
        ? Math.round(data.totalAmount / data.vouchersPerDay.length)
        : 0;
    const abnormalDays = (data.vouchersPerDay || []).filter(d =>
        d.amount > burnRate * 1.5
    );
    const topEmployees = [...(data.vouchersByParty || [])]
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 5);

    const topEmployee = topEmployees[0];

    const insights = [];

    if (trend.totalAmount) {
        insights.push(
            trend.totalAmount.isUp
                ? `Spending increased by ${trend.totalAmount.value.toFixed(1)}%`
                : `Spending decreased by ${trend.totalAmount.value.toFixed(1)}%`
        );
    }

    if (topEmployee) {
        insights.push(
            `Highest expense by ${topEmployee.name} (₹ ${topEmployee.amount})`
        );
    }

    if (abnormalDays.length > 0) {
        insights.push(
            `${abnormalDays.length} abnormal spending day(s) detected`
        );
    }

    if (burnRate > 0) {
        insights.push(
            `Average spend is ₹ ${burnRate} per day`
        );
    }
    const renderTrend = (t) => {
        if (!t) return null;

        return (
            <div style={{
                ...styles.trend,
                color: t.isUp ? "#2ed573" : "#ff4757"
            }}>
                {t.isUp ? "↑" : "↓"}
                {Math.abs(t.value || 0).toFixed(1)}%
            </div>
        );
    };
    
    const lineData = data.vouchersPerDay || [];
    const partyData = data.vouchersByParty || [];
    const pieData = data.categorySplit || [];
    
    const [activeFilter, setActiveFilter] = useState("");
    const handleLast7Days = () => {
        const today = new Date();
        const last7 = new Date();
        last7.setDate(today.getDate() - 7);

        const from = last7.toISOString().split("T")[0];
        const to = today.toISOString().split("T")[0];

        setFromDate(from);
        setToDate(to);
        loadData(from, to);   // 🔥 ADD THIS
        setTimeout(() => {
            setActiveFilter("last7");
        }, 0);
    };
    const handleReset = () => {
        setFromDate("");
        setToDate("");
        setPaidTo("");
        setActiveFilter("");

        setData({
            totalVouchers: 0,
            totalAmount: 0,
            vouchersPerDay: [],
            vouchersByParty: [],
            categorySplit: []
        });

        setTrend({});
    };
    return (
        <div style={styles.container}>

            {/* HEADER */}
            <div style={styles.header}>
                <h2>Voucher - Dashboard</h2>
            </div>

            {/* FILTER */}
            <div style={styles.filterRow}>

                <input
                    type="date"
                    value={fromDate}
                    onChange={e => {
                        setFromDate(e.target.value);
                        setActiveFilter("");
                    }}
                    style={styles.input}
                />

                <input
                    type="date"
                    value={toDate}
                    onChange={e => {
                        setToDate(e.target.value);
                        setActiveFilter("");
                    }}
                    style={styles.input}
                />

                <TextField
                    label="Paid To"
                    value={paidTo}
                    onChange={(e) => setPaidTo(e.target.value)}
                    size="small"
                    sx={styles.inputStyle}
                />

                <button style={styles.filterBtn} onClick={() => loadData()}>
                    Filter
                </button>

                <button
                    style={activeFilter === "last7" ? styles.last7BtnActive : styles.last7Btn}
                    onClick={handleLast7Days}
                >
                    Last 7 Days
                </button>

                <button style={styles.resetBtn} onClick={handleReset}>
                    Reset
                </button>

            </div>

            {/* CARDS */}
            <div style={styles.cardRow}>

                {/* TOTAL VOUCHERS */}
                <div
                    style={{
                        ...styles.cardBase,
                        ...styles.cardTotal,
                        ...(hoveredCard === "total" && styles.cardHover)
                    }}
                    onMouseEnter={() => setHoveredCard("total")}
                    onMouseLeave={() => setHoveredCard(null)}
                >
                    <div style={styles.cardContent}>
                        <FaFileInvoice size={16} />
                        <h3 style={{ marginBottom: "10px", fontSize: "13px" }}>
                            Total Vouchers
                        </h3>
                        {/*<h3 style={styles.cardTitle}>Total Vouchers</h3>*/}
                    </div>

                    <p style={styles.cardValueText}>{data.totalVouchers}</p>
                </div>

                {/* TOTAL AMOUNT */}
                <div
                    style={{
                        ...styles.cardBase,
                        ...styles.cardValue,
                        ...(hoveredCard === "amount" && styles.cardHover)
                    }}
                    onMouseEnter={() => setHoveredCard("amount")}
                    onMouseLeave={() => setHoveredCard(null)}
                >
                    <div style={styles.cardContent}>
                        <FaRupeeSign size={16} />
                        <h3 style={styles.cardTitle}>Total Amount</h3>
                    </div>

                    <p style={styles.cardValueText}>
                        ₹ {data.totalAmount.toLocaleString("en-IN")}
                    </p>
                </div>

                {/* AVG AMOUNT */}
                <div
                    style={{
                        ...styles.cardBase,
                        background: "linear-gradient(135deg, #3742fa, #1e90ff)",
                        ...(hoveredCard === "avg" && styles.cardHover)
                    }}
                    onMouseEnter={() => setHoveredCard("avg")}
                    onMouseLeave={() => setHoveredCard(null)}
                >
                    <div style={styles.cardContent}>
                        <FaRupeeSign size={16} />
                        <h3 style={styles.cardTitle}>Avg Amount</h3>
                    </div>

                    <p style={styles.cardValueText}>₹ {avgAmount}</p>
                </div>
                <div
                    style={{
                        ...styles.cardBase,
                        background: "linear-gradient(135deg, #ff9f43, #ff6b6b)",
                        ...(hoveredCard === "burn" && styles.cardHover)
                    }}
                    onMouseEnter={() => setHoveredCard("burn")}
                    onMouseLeave={() => setHoveredCard(null)}
                >
                    <div style={styles.cardContent}>
                        <h3 style={styles.cardTitle}>Burn Rate</h3>
                    </div>
                    {/*<p style={styles.cardValueText}>₹ {burnRate} / day</p>*/}
                    <p style={styles.cardValueText}>
                        ₹ {burnRate.toLocaleString("en-IN")} / day
                    </p>
                </div>
            </div>
            <hr style={{ margin: "15px 0", border: "none", borderTop: "1px solid #eee" }} />
            {abnormalDays.length > 0 && (
                <div style={{
                    marginTop: "10px",
                    padding: "8px",
                    background: "#ff4757",
                    color: "#fff",
                    borderRadius: "4px",
                    fontWeight: "bold"
                }}>
                    ⚠ High spending detected on {abnormalDays.length} day(s)
                </div>
            )}
            {/* ✅ ADD HERE — STEP 10 */}
            {insights.length > 0 && (
                <div style={{
                    marginTop: "16px",
                    padding: "16px",
                    borderRadius: "12px",
                    background: "#f8f9ff",
                    border: "1px solid #e0e4ff",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "16px"
                }}>

                    {/* LEFT ICON */}
                    <div style={{
                        width: "50px",
                        height: "50px",
                        borderRadius: "50%",
                        background: "linear-gradient(135deg, #6c5ce7, #3742fa)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontSize: "22px",
                        flexShrink: 0
                    }}>
                        💡
                    </div>

                    {/* RIGHT CONTENT */}
                    <div style={{ flex: 1 }}>

                        <div style={{
                            fontWeight: "bold",
                            fontSize: "14px",
                            marginBottom: "6px",
                            color: "#3742fa"
                        }}>
                            Insights
                        </div>

                        <ul style={{
                            margin: 0,
                            paddingLeft: "18px",
                            fontSize: "13px",
                            lineHeight: "1.6"
                        }}>
                            {insights.map((ins, i) => (
                                <li key={i}>{ins}</li>
                            ))}
                        </ul>

                    </div>
                </div>
            )}
            {/* CHARTS */}
            <div style={styles.chartRow}>

                {/* LINE */}
                <div style={styles.chartBox}>
                    <h3>Vouchers Per Day</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={lineData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis />
                            <Tooltip />
                            <Line type="monotone" dataKey="amount" stroke="#3742fa" />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                {/* BAR */}
                <div style={styles.chartBox}>
                    <h3>Expense by Party</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={partyData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="amount" fill="#2ed573" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* PIE */}
                <div style={styles.chartBox}>
                    <h3>Category Split</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={100}>
                                {pieData.map((entry, index) => (
                                    <Cell key={index} fill={`hsl(${index * 40},70%,60%)`} />
                                ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
                <div style={styles.chartBox}>
                    <h3>Top 5 Employees (Expense)</h3>

                    <BarChart width={400} height={250} data={topEmployees}>
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="amount">
                            {topEmployees.map((entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={
                                        ["#2ed573", "#1e90ff", "#ffa502", "#ff6b6b", "#3742fa"][index % 5]
                                    }
                                />
                            ))}
                        </Bar>
                    </BarChart>
                </div>
            </div>
            {topEmployee && (
                <div style={{ marginTop: "10px", fontWeight: "bold" }}>
                    🔥 Highest Expense By: {topEmployee.name} (₹ {topEmployee.amount})
                </div>
            )}
        </div>
    );
}
const styles = {

    /* MAIN CONTAINER */
    container: {
        padding: "16px",
        fontFamily: "Verdana",
        fontSize: "12px",
        maxWidth: "1400px",
        margin: "0 auto",
        boxSizing: "border-box"
    },

    /* HEADER */
    header: {
        background: "#2f3542",
        color: "#fff",
        padding: "6px",
        borderRadius: "6px",
        marginBottom: "10px"
    },

    /* FILTER ROW */
    filterRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: "12px",
        alignItems: "center",
        marginBottom: "14px"
    },

    /* DATE INPUT */
    input: {
        height: "32px",
        padding: "0 8px",
        borderRadius: "4px",
        border: "1px solid #ccc",
        fontSize: "12px",
        fontFamily: "Verdana"
    },

    /* MUI INPUT (Paid To) */
    inputStyle: {
        minWidth: "140px",
        flex: "1 1 150px",
        "& .MuiOutlinedInput-root": {
            height: "32px",
            fontSize: "12px",
            fontFamily: "Verdana"
        },
        "& .MuiInputBase-input": {
            padding: "8px 10px",
            fontFamily: "Verdana",
            fontSize: "12px"
        },
        "& .MuiInputLabel-root": {
            fontFamily: "Verdana",
            fontSize: "12px"
        }
    },

    /* BUTTONS */
    filterBtn: {
        height: "32px",
        background: "#2ed573",
        border: "none",
        padding: "0 12px",
        color: "#fff",
        borderRadius: "4px",
        cursor: "pointer",
        fontSize: "12px",
        fontFamily: "Verdana"
    },

    last7Btn: {
        height: "32px",
        background: "#2ed573",
        border: "none",
        padding: "0 12px",
        color: "#fff",
        borderRadius: "4px",
        cursor: "pointer",
        fontSize: "12px",
        fontFamily: "Verdana"
    },

    last7BtnActive: {
        height: "32px",
        background: "#145a32",
        border: "1px solid #0e3d24",
        padding: "0 12px",
        color: "#fff",
        borderRadius: "4px",
        cursor: "pointer",
        fontSize: "12px",
        fontFamily: "Verdana",
        boxShadow: "inset 0 0 3px rgba(0,0,0,0.4)"
    },

    resetBtn: {
        height: "32px",
        background: "#ff4757",
        border: "none",
        padding: "0 12px",
        color: "#fff",
        borderRadius: "4px",
        cursor: "pointer",
        fontSize: "12px",
        fontFamily: "Verdana"
    },

    /* CARD ROW */
    cardRow: {
        display: "flex",
        gap: "14px",
        marginTop: "12px",
        flexWrap: "wrap"
    },
    /* BASE CARD */
    cardBase: {
        padding: "12px 10px",
        borderRadius: "10px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        flex: "1 1 180px",
        textAlign: "center",
        color: "#fff",
        cursor: "pointer",
        transition: "all 0.25s ease"
    },

    /* CARD COLORS */
    cardTotal: {
        background: "linear-gradient(135deg, #57606f, #2f3542)"
    },

    cardValue: {
        background: "linear-gradient(135deg, #2ed573, #1eae60)"
    },

    /* CARD CONTENT */
    cardContent: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        marginBottom: "4px"
    },

    cardTitle: {
        margin: 0,
        fontSize: "12px",
        fontWeight: "500"
    },

    cardValueText: {
        margin: 0,
        fontSize: "15px",
        fontWeight: "bold"
    },

    /* HOVER EFFECT */
    cardHover: {
        transform: "translateY(-5px)",
        boxShadow: "0 10px 22px rgba(0,0,0,0.18)"
    },

    /* TREND */
    trend: {
        fontSize: "11px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "3px",
        marginTop: "3px",
        fontWeight: "bold"
    },

    /* CHARTS */
    chartRow: {
        display: "flex",
        gap: "20px",
        marginTop: "20px",
        flexWrap: "wrap"
    },

    chartBox: {
        background: "#ffffff",
        padding: "16px",
        borderRadius: "10px",
        boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
        flex: "1 1 420px",
        minWidth: "320px"
    },
};
export default VoucherDashboard;